/**
 * Feldlisten-Karte für EIN Content-Block-Feld (C1 + Variant-D-Veredelung).
 *
 * Header (Label/„nur"-Badge/Abweichung/Zähler/Versionen/KI) + Vorschau-Banner +
 * Wert (Textarea bzw. Bullets) + Master-Ribbon (Override) + Per-Feld-Save +
 * git-Versionslog (Vorschau-im-Feld / Wiederherstellen / Vergleichen).
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'
import { Button, Textarea, Loading } from '@/components/v2/ui'
import { getBlockTypeInfo } from '@/hooks/useContentBlock'
import { useContentBlockFieldSave } from '@/hooks/useContentBlockFieldSave'
import { EID } from '@/lib/element-ids.registry'
import { FieldHeader } from './FieldHeader'
import { FieldPreviewBanner } from './FieldPreviewBanner'
import { FieldVersionDropdown } from './FieldVersionDropdown'
import { MasterRibbon } from './MasterRibbon'
import { BulletsFieldEditor } from './BulletsFieldEditor'
import { readLangText } from './contentBlockLang'
import type { ContentBlock } from '@/types/content-blocks'
import type { ContentBlockVersion } from '@/types/versioning'

interface Props {
  block: ContentBlock
  lang: string
  maxLength?: number
  productId: string
  channelPackageId: string | null
  channelId?: string
  isJTLChannel: boolean
  onCompare: () => void
  deviation?: 'override' | 'inherited' | null
  highlighted?: boolean
  masterValue?: string
  restrictionNote?: string
}

export function ContentBlockFieldCard({
  block,
  lang,
  maxLength,
  productId,
  channelPackageId,
  channelId,
  isJTLChannel,
  onCompare,
  deviation = null,
  highlighted = false,
  masterValue,
  restrictionNote,
}: Props) {
  const { t } = useTranslation()
  const info = getBlockTypeInfo(block.block_type)
  const initial = readLangText(block.current_version?.content, lang)
  const [value, setValue] = useState(initial)
  const [showVersions, setShowVersions] = useState(false)
  const [previewVersion, setPreviewVersion] = useState<ContentBlockVersion | null>(null)
  const { save, isSaving } = useContentBlockFieldSave(block)

  useEffect(() => {
    setValue(readLangText(block.current_version?.content, lang))
    setPreviewVersion(null)
  }, [block, lang])

  const isInherited = deviation === 'inherited'
  const isBullets = block.block_type === 'bullets'
  const isPreviewing = previewVersion != null
  const displayValue = previewVersion ? readLangText(previewVersion.content, lang) : value
  const readOnly = isInherited || isPreviewing
  const dirty = value !== initial

  const handleSave = async () => {
    const ok = await save({ productId, channelPackageId, isJTLChannel, lang, text: value })
    if (ok) setShowVersions(false)
  }

  return (
    <section
      className={`content-field-card rounded-lg border p-3 transition-shadow ${highlighted ? 'border-primary-500 ring-2 ring-primary-300 dark:ring-primary-700' : isPreviewing ? 'border-amber-300 dark:border-amber-700/60' : 'border-neutral-200 dark:border-neutral-800'}`}
      aria-label={info.label}
    >
      <FieldHeader
        label={info.label}
        restrictionNote={restrictionNote}
        deviation={deviation}
        counterLen={isBullets ? 0 : displayValue.length}
        max={isBullets ? undefined : maxLength}
        blockType={block.block_type}
        versionsOpen={showVersions}
        onToggleVersions={() => setShowVersions((v) => !v)}
        productId={productId}
        channelId={channelId}
        canShowVersions={!!block.id}
      />

      {previewVersion && (
        <FieldPreviewBanner
          version={previewVersion}
          onCancel={() => setPreviewVersion(null)}
          onRestore={() => {
            setValue(readLangText(previewVersion.content, lang))
            setPreviewVersion(null)
          }}
        />
      )}

      {isBullets ? (
        <BulletsFieldEditor block={block} lang={lang} />
      ) : (
        <Textarea
          rows={block.block_type === 'description' ? 6 : 2}
          value={displayValue}
          onChange={(e) => setValue(e.target.value)}
          placeholder={info.description}
          aria-label={info.label}
          readOnly={readOnly}
          className={readOnly ? 'bg-neutral-50 dark:bg-neutral-900/60 text-neutral-600 dark:text-neutral-400' : undefined}
        />
      )}

      {deviation === 'override' && masterValue && !isPreviewing && !isBullets && (
        <MasterRibbon masterValue={masterValue} onAdopt={() => setValue(masterValue)} />
      )}

      {dirty && !isInherited && !isPreviewing && !isBullets && (
        <div className="flex items-center gap-2 mt-2">
          <Button variant="primary" size="sm" id={`${EID['content-field-save-btn']}-${block.block_type}`} onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Loading size="sm" /> : <Check className="h-4 w-4 mr-1" />}
            {t('common.save')}
          </Button>
          <Button variant="outline" size="sm" id={`${EID['content-field-discard-btn']}-${block.block_type}`} onClick={() => setValue(initial)} disabled={isSaving}>
            {t('common.cancel')}
          </Button>
        </div>
      )}

      {showVersions && block.id && (
        <FieldVersionDropdown
          blockId={block.id}
          lang={lang}
          currentVersionId={block.current_version_id}
          previewVersionId={previewVersion?.id ?? null}
          onPreview={(v) => setPreviewVersion(v)}
          onRestore={(text) => {
            setValue(text)
            setPreviewVersion(null)
            setShowVersions(false)
          }}
          onCompare={onCompare}
        />
      )}
    </section>
  )
}
